// Demo steps 3 to 5 for one batch: evaluate as_is or assume_open (named assumptions), re-run, compare, publish.
import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { PageHeader } from '../components/Layout.tsx';
import { Link, useParams } from 'react-router';
import { DemoStamp, ErrorBox, Loading } from '../components/ui.tsx';
import { Obligations } from '../components/Obligations.tsx';
import { VerdictView } from '../components/VerdictView.tsx';
import {
  activePack,
  demoScenarios,
  evaluationsFor,
  myRoles,
  passports,
  type DemoScenario,
  type PassportRow,
  type ScheduledRuleDef,
} from '../lib/data.ts';
import { invoke } from '../lib/supabase.ts';
import type { AssumptionEntry, EvaluationRow } from '../lib/types.ts';

export function BatchDetail({ compare = false }: { compare?: boolean }) {
  const { t } = useTranslation();
  const { id = '' } = useParams();
  const [evals, setEvals] = useState<EvaluationRow[] | null>(null);
  const [catalogue, setCatalogue] = useState<AssumptionEntry[]>([]);
  const [rules, setRules] = useState<ScheduledRuleDef[]>([]);
  const [chosen, setChosen] = useState<string[]>([]);
  const [roles, setRoles] = useState<string[]>([]);
  const [published, setPublished] = useState<string | null>(null);
  const [passport, setPassport] = useState<PassportRow | null>(null);
  const [scenarios, setScenarios] = useState<DemoScenario[]>([]);
  const [error, setError] = useState<unknown>(null);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    try {
      setEvals(await evaluationsFor(id));
      const pack = await activePack();
      setCatalogue(pack?.pack.route_model.assumption_catalogue ?? []);
      setRules(pack?.pack.rules.items ?? []);
      setRoles(await myRoles());
      setPassport((await passports()).get(id) ?? null);
      setScenarios((await demoScenarios()).filter((x) => x.batch_id === id && x.mode === 'assume_open'));
    } catch (e) {
      setError(e);
    }
  }, [id]);
  useEffect(() => {
    void load();
  }, [load]);

  async function run(fn: 'evaluate' | 'publish', mode: string, assumptions: string[]) {
    setBusy(true);
    setError(null);
    try {
      const r = await invoke<{ upi?: string }>(fn, { batch_id: id, mode, assumptions });
      if (r.upi) setPublished(r.upi);
      await load();
    } catch (e) {
      setError(e);
    } finally {
      setBusy(false);
    }
  }

  if (!evals) return <Loading />;
  const latestAsIs = evals.find((e) => e.mode === 'as_is');
  const latestOpen = evals.find((e) => e.mode === 'assume_open');
  const canAssume = roles.some((r) => ['regulatory_owner', 'auditor', 'platform_admin'].includes(r));
  const canPublish = roles.some((r) => ['regulatory_owner', 'platform_admin'].includes(r));
  const latest = evals[0];
  // the publish function re-evaluates and refuses anything but PASS or WARN; the button says so before a refusal
  const publishable = latest && ['PASS', 'WARN'].includes(latest.verdict.verdict);

  return (
    <main>
      <p className="crumbs">
        <Link to="/batches">← {t('nav.batches')}</Link> ·{' '}
        <Link to={`/batches/${id}`}>{t('batch.overview')}</Link> ·{' '}
        <Link to={`/batches/${id}/genealogy`}>{t('batch.genealogy')}</Link> ·{' '}
        <Link to={`/batches/${id}/compare`}>{t('batch.compare')}</Link>
      </p>
      <PageHeader title={id} page={compare ? 'compare' : 'batch'}>
        <button disabled={busy} onClick={() => void run('evaluate', 'as_is', [])}>
          {t('batch.evaluate_as_is')}
        </button>
        {latest && (
          <button
            className="secondary"
            disabled={busy}
            onClick={() => void run('evaluate', latest.mode, latest.assumptions)}
          >
            {t('batch.rerun')}
          </button>
        )}
        {canPublish && latest && (
          <button
            className="secondary"
            disabled={busy || !publishable}
            aria-describedby="publish-note"
            onClick={() => void run('publish', latest.mode, latest.assumptions)}
          >
            {t('batch.publish')} ({latest.mode})
          </button>
        )}
      </PageHeader>
      {canPublish && latest && !publishable && (
        <p className="hint" id="publish-note">
          {t('batch.publish_blocked', { verdict: latest.verdict.verdict })}
        </p>
      )}
      {passport && (
        <p className="hint">
          {t(passport.demonstration ? 'batch.has_passport_demo' : 'batch.has_passport', {
            date: passport.published_at.slice(0, 10),
          })}{' '}
          <Link to={`/p/${passport.upi}`}>{t('batch.view_passport')}</Link>
        </p>
      )}
      <ErrorBox error={error} />
      {canAssume && (
        <details className="card assumptions-card">
          <summary>{t('batch.assumptions')}</summary>
          <p className="muted small">{t('batch.assumptions_help')}</p>
          {scenarios.length > 0 && (
            <p className="scenario-sets">
              {scenarios.map((x) => (
                <button
                  key={x.name}
                  type="button"
                  className="secondary small"
                  onClick={() =>
                    // only the keys this pack offers; a suspended gate stays closed whatever a scenario lists
                    setChosen(
                      x.assumptions
                        .filter((k) => catalogue.some((a) => a.key === k && !a.gate_suspended))
                        .sort(),
                    )
                  }
                >
                  {t('batch.use_scenario', { test: x.test, title: x.title })}
                </button>
              ))}
            </p>
          )}
          <fieldset className="assumptions">
            {catalogue.map((a) => (
              <label key={a.key} title={`${a.real_world_status} ${a.what_would_change_it}`}>
                <input
                  type="checkbox"
                  checked={chosen.includes(a.key)}
                  disabled={a.gate_suspended}
                  onChange={(ev) =>
                    setChosen(
                      ev.target.checked ? [...chosen, a.key].sort() : chosen.filter((k) => k !== a.key),
                    )
                  }
                />{' '}
                <code>{a.key}</code> → {a.closes_gate}{' '}
                <span className="muted small">({a.real_world_status})</span>
              </label>
            ))}
            <DemoStamp count={chosen.length} />
            <button
              disabled={busy || chosen.length === 0}
              onClick={() => void run('evaluate', 'assume_open', chosen)}
            >
              {t('batch.evaluate_assume_open')}
            </button>
          </fieldset>
        </details>
      )}
      {published && (
        <p className="ok">
          {t('batch.published')} <Link to={`/p/${published}`}>{published}</Link> — {t('batch.open_passport')}
        </p>
      )}
      {!compare && latestAsIs && <Obligations verdict={latestAsIs.verdict} rules={rules} />}
      {compare ? (
        <div className="side-by-side">
          <div>
            <h2>as_is</h2>
            {latestAsIs ? (
              <VerdictView e={latestAsIs} compact />
            ) : (
              <p className="muted">{t('batch.not_evaluated')}</p>
            )}
          </div>
          <div className="demo-column">
            <h2>assume_open</h2>
            {latestOpen ? (
              <VerdictView e={latestOpen} compact />
            ) : (
              <p className="muted">{t('batch.not_evaluated')}</p>
            )}
          </div>
        </div>
      ) : latest ? (
        <VerdictView e={latest} />
      ) : (
        <p className="muted">{t('batch.not_evaluated')}</p>
      )}
    </main>
  );
}
